import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Message, ContextUsage } from '../types';
import { runChatTurn, AccessNotProvisionedError } from '../services/chat';
import { AccessRequestWall, AccessWallReason } from '../components/AccessRequestWall';
import { PlansWelcome } from '../components/PlansWelcome';
import { PlanBadge } from '../components/PlanBadge';
import { UpsellBanner } from '../components/UpsellBanner';
import { fetchAuthProfile, type AuthProfile, type PlanTier } from '../services/authProfile';
import { requiredTierForSkill, meetsTier } from '../services/featureGates';
import { ingestCurrentWorkbook } from '../services/ingest';
import { IngestSummary } from '../types/ingest';
import { uploadDocument, listAttachedDocuments, deleteAttachedDocument, isAcceptableUploadType, type UploadedDocument } from '../services/documents';
import { Markdown } from './Markdown';
import { ContextMeter } from './ContextMeter';
import {
  clearAllSnapshots,
  setQuerySnapshotSession,
  buildDashboardFromWizard,
  setSessionExpiredCallback,
  getPendingAskUser,
  resolveAskUser,
  subscribePendingAskUser,
  clearPendingAskUser,
  runCommentary,
  // Renamed locally — App.tsx defines a useState pair where the SETTER is
  // also `setSessionLogEnabled`. Without the alias, the import was being
  // shadowed and the office.ts singleton never received the toggle.
  setSessionLogEnabled as setOfficeSessionLogEnabled,
} from '../tools/office';
import { toolLabel } from '../tools/labels';
import { SKILLS, filterSkills, getSkillByExactSlash, Skill } from '../services/skills';
import { SettingsModal } from '../components/SettingsModal';
import { UserProfile, loadUserProfile, saveUserProfile } from '../services/userProfile';
import { DashboardWizard } from '../components/DashboardWizard';
import {
  questionnaireToScaffold,
  WizardAnswers,
  formatWizardPrompt,
  formatWizardResultMarkdown,
} from '../services/questionnaireToScaffold';
import { buildResumeMessage, deriveCheckpoint, renderCheckpointAsPrimer } from '../services/checkpoint';
import {
  putCheckpoint as putCheckpointApi,
  listCheckpoints as listCheckpointsApi,
  fetchCheckpoint as fetchCheckpointApi,
  deleteCheckpoint as deleteCheckpointApi,
  CheckpointListItem,
} from '../services/checkpointApi';
import {
  putConversation as putConversationApi,
  listConversations as listConversationsApi,
  fetchConversation as fetchConversationApi,
  deleteConversation as deleteConversationApi,
} from '../services/conversationApi';

const CONSENT_KEY = 'practiceiq.consent.v2';
const LEGACY_HISTORY_KEY = 'practiceiq.history.v1';
const CONVERSATIONS_KEY = 'practiceiq.conversations.v2';
const ACTIVE_CONVO_KEY = 'practiceiq.activeConvo.v2';
const CONVO_PREFIX = 'practiceiq.convo.v2.';
const HISTORY_MAX_BYTES = 256 * 1024;
const MAX_CONVERSATIONS = 50;
// Phase C.4 — one-time trust warning before ingest. Mirrors Claude desktop's
// "files can contain hidden instructions" splash; one-time per device.
const TRUST_ACK_KEY = 'practiceiq.trustAcknowledged.v1';

interface ToolCallDisplay {
  name: string;
  // Tool-use id from Bedrock — needed to key UI state for tools that
  // interact with the user (Phase A.3 ask_user). Undefined while streaming;
  // populated when the assistant message finalises.
  id?: string;
  // Tool-use input, captured when the assistant message completes streaming.
  // Used for inline code preview on execute_office_js (Phase A.1) — and any
  // future tool that wants per-call rendering. Undefined while streaming.
  input?: unknown;
}

interface DisplayMsg {
  role: 'user' | 'assistant';
  text: string;
  toolCalls: ToolCallDisplay[];
  streaming?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  history: Message[];
  display: DisplayMsg[];
  createdAt: string;
  updatedAt: string;
  ingest?: IngestSummary;     // Move 1: persisted per-conversation
}

interface ConversationMeta {
  id: string;
  title: string;
  updatedAt: string;
}

function newConvoId(): string {
  return 'c_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function deriveTitle(history: Message[]): string {
  for (const m of history) {
    if (m.role !== 'user') continue;
    for (const b of m.content) {
      if (b.type === 'text' && b.text.trim()) {
        const t = b.text.trim().replace(/\s+/g, ' ');
        return t.length > 60 ? t.slice(0, 57) + '…' : t;
      }
    }
  }
  return 'New chat';
}

// 2026-06-16 — cheap content signature used to decide whether a conversation
// actually changed since the last server save (or since it was loaded). Keyed
// on message counts + the last bubble's size, which captures growth and
// last-message edits without serialising the whole transcript.
function transcriptSig(display: DisplayMsg[], historyLen: number): string {
  const last = display[display.length - 1];
  return `${display.length}|${historyLen}|${(last?.text ?? '').length}|${last?.toolCalls?.length ?? 0}`;
}

// 2026-06-16 — deleted-conversation tombstones. When a chat is deleted we
// record its id so the server list-merge can't resurrect it (e.g. if the
// server DELETE was offline/in-flight at the time of the next merge). Bounded.
const DELETED_CONVOS_KEY = 'practiceiq.deletedConvos.v1';
function loadDeletedConvoIds(): Set<string> {
  try {
    const raw = localStorage.getItem(DELETED_CONVOS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch { return new Set(); }
}
function addDeletedConvoId(id: string): void {
  try {
    const ids = loadDeletedConvoIds();
    ids.add(id);
    // Keep the most recent 200 to bound growth.
    const arr = Array.from(ids).slice(-200);
    localStorage.setItem(DELETED_CONVOS_KEY, JSON.stringify(arr));
  } catch { /* private mode */ }
}

function loadConversationsList(): ConversationMeta[] {
  try {
    const raw = localStorage.getItem(CONVERSATIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as ConversationMeta[];
  } catch { return []; }
}

function saveConversationsList(metas: ConversationMeta[]): void {
  try {
    localStorage.setItem(CONVERSATIONS_KEY, JSON.stringify(metas));
  } catch { /* storage full / disabled */ }
}

function loadConversation(id: string): Conversation | null {
  try {
    const raw = localStorage.getItem(CONVO_PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Conversation;
    if (!parsed?.history || !parsed?.display) return null;
    // Migrate old-format toolCalls (string[]) to new format (ToolCallDisplay[]).
    // Before Phase A.3, display entries stored tool names as strings; today
    // they're {name, id?, input?} objects. Loading old data without this
    // migration leaves call.name === undefined and renders break silently.
    for (const m of parsed.display) {
      if (!Array.isArray(m.toolCalls)) {
        m.toolCalls = [];
        continue;
      }
      m.toolCalls = m.toolCalls.map((tc: unknown) =>
        typeof tc === 'string'
          ? { name: tc }
          : (tc && typeof tc === 'object' && typeof (tc as { name?: unknown }).name === 'string'
              ? (tc as { name: string; id?: string; input?: unknown })
              : { name: 'unknown' })
      );
    }
    return parsed;
  } catch { return null; }
}

function saveConversation(convo: Conversation): void {
  try {
    let serialised = JSON.stringify(convo);
    if (serialised.length > HISTORY_MAX_BYTES) {
      const trimmed: Conversation = {
        ...convo,
        history: convo.history.slice(-20),
        display: convo.display.slice(-30),
      };
      serialised = JSON.stringify(trimmed);
    }
    localStorage.setItem(CONVO_PREFIX + convo.id, serialised);
  } catch { /* storage full / disabled */ }
}

function deleteConversation(id: string): void {
  try { localStorage.removeItem(CONVO_PREFIX + id); } catch { /* ignore */ }
}

function getActiveConvoId(): string | null {
  try { return localStorage.getItem(ACTIVE_CONVO_KEY); } catch { return null; }
}

function setActiveConvoId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_CONVO_KEY, id);
    else localStorage.removeItem(ACTIVE_CONVO_KEY);
  } catch { /* ignore */ }
}

// One-time migration from the old single-session key. If a v1 session exists
// and no v2 conversations have been written yet, promote it to a v2 conversation.
function migrateLegacyIfNeeded(): void {
  try {
    if (loadConversationsList().length > 0) return;
    const raw = localStorage.getItem(LEGACY_HISTORY_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed?.history || !parsed?.display) return;
    const id = newConvoId();
    const now = parsed.savedAt || new Date().toISOString();
    const convo: Conversation = {
      id,
      title: deriveTitle(parsed.history),
      history: parsed.history,
      display: parsed.display,
      createdAt: now,
      updatedAt: now,
    };
    saveConversation(convo);
    saveConversationsList([{ id, title: convo.title, updatedAt: convo.updatedAt }]);
    setActiveConvoId(id);
    localStorage.removeItem(LEGACY_HISTORY_KEY);
  } catch { /* ignore */ }
}

// Move 5 follow-up Step 10 — items can be either a 'wizard' (opens the
// modal directly) or a 'prompt' (fills the input box for the user to send).
// Section dividers (kind: 'divider') visually group related actions in
// the popover. Default kind is 'prompt' for backwards-compat with the
// previous flat array.
type QuickActionItem =
  | { kind: 'wizard'; emoji: string; label: string; hint?: string }
  | { kind?: 'prompt'; emoji: string; label: string; prompt: string }
  | { kind: 'divider'; label?: string };

const QUICK_ACTIONS: QuickActionItem[] = [
  // ---- Wizard (special — opens the structured questionnaire) ----
  { kind: 'wizard', emoji: '🪄', label: 'Build dashboard', hint: '6 questions, then a board-pack dashboard built directly. No agent guessing.' },
  { kind: 'divider', label: 'Dashboards' },

  // ---- Pre-built dashboards (chat the agent into the right archetype) ----
  { emoji: '📊', label: 'Monthly pack',           prompt: "Build me a monthly management report from my current selection or active sheet. Apply the dashboarding skill: navy banner, KPI tiles with variance arrows, P&L vs budget vs prior year, conditional-format the variance column, 3-paragraph commentary, watch items, disclaimer. Tell me where each block lives once done." },
  { emoji: '💰', label: '13-week cash',           prompt: "Build me a 13-week cash forecast from my data. Apply the cash management skill: weekly columns; receipts split (customer / other); payments split (payroll incl. super, AP, ATO, debt service, other opex, capex); flag the lowest cash point and any facility breach risk; AR + AP ageing buckets; DSO/DPO/CCC trend if data permits; runway." },
  { emoji: '🏗',  label: 'Forecast model',        prompt: "Build me a 12-month P&L forecast model with adjustable assumption cells. Apply the modeling skill: assumption block at top with input-yellow fill (#FFF2CC), historical actuals as values, forecast columns as formulas referencing absolute assumption cells, named ranges, BS / CF / RE integrity checks, and protect the formula cells while leaving inputs editable." },
  { emoji: '🧮', label: '3-statement',            prompt: "Build me a 3-statement model (P&L, BS, CF) integrated through retained earnings, working-capital, and cash. Apply the modeling skill conventions: cell colour code, named ranges, three integrity checks (BS balances, RE reconciles, CF cash = BS cash). Use my current data as the historical anchor." },
  { emoji: '✨', label: 'KPI scorecard',          prompt: "Build me an executive KPI scorecard dashboard from this workbook (executive_kpi_scorecard archetype). 3 headline gauges (Revenue / Net margin% / Net income) + 8 KPI tiles in a 4×2 grid with sparklines + a compact P&L summary + trend chart. C-level audience." },
  { emoji: '📅', label: 'Annual P&L vs PY',       prompt: "Build me an annual P&L vs prior-year dashboard from this workbook (annual_pl_compare archetype). KPI summary + revenue breakdown + expense breakdown + profitability + 4 charts in a 2×2 grid below the data on a fresh print page (page break, landscape, fit-to-page-width)." },
  { emoji: '🏬', label: 'Dept compare',           prompt: "Build me a department comparison dashboard (department_compare archetype) — pick up to 6 sheets and show headline KPI tiles per dept + side-by-side metric × dept table with heatmap + clustered column comparison chart. Skip if I have only one comparable sheet." },
  { emoji: '⚠️', label: 'Risk register',          prompt: "Build me a risk and compliance register (compliance_risk_register archetype). 4 status tiles + 5×5 likelihood-impact heatmap + risk register table with traffic-light status, dropdown validations on Likelihood / Impact / Status, and seeded example rows I can replace." },
  { kind: 'divider', label: 'Analysis' },

  // ---- Analytical chat actions (answers + insights, not multi-step builds) ----
  { emoji: '🧾', label: 'Variance bridge',        prompt: "Generate a variance bridge from my current selection (actual vs budget). Decompose into volume/price/mix where data supports; apply 10% or $10k materiality; output a waterfall chart and attributable commentary. Apply the variance-analysis skill." },
  { emoji: '🔝', label: 'Top drivers',            prompt: "Find the 5 lines with the biggest period-over-period movement on my active sheet — both absolute dollars and percentage. For each, explain in one sentence what's most likely driving the change. CFO-ready language, no jargon padding." },
  { emoji: '💼', label: 'Working capital',        prompt: "Compute working capital position on my active sheet: DSO, DPO, CCC, and AR + AP ageing if those columns exist. Identify customer or supplier concentration risk. Flag any covenant exposure. Apply the cash management skill." },
  { emoji: '🚀', label: 'Burn & runway',          prompt: "Compute monthly burn rate, runway in months, break-even month, and projected cash-out date from this workbook. Plain numbers + one-paragraph commentary. If I have a 13-week or monthly cash sheet, use it; otherwise infer from the P&L cash position." },
  { emoji: '📈', label: 'Financial review',       prompt: "Run a financial analysis on my current selection. Apply the financial-analysis skill: GP%, EBITDA margin, working-capital ratios, leverage, returns, trend. CFO-ready narrative with 3–5 number-anchored observations and 2–3 watch items." },
  { emoji: '💡', label: 'Plain English',          prompt: "Explain the active sheet to someone who isn't an accountant. 3 paragraphs, no jargon. Focus on what's working, what isn't, and what the reader should ask about next." },
  { kind: 'divider', label: 'Compliance & data hygiene' },

  // ---- Compliance + analyst hygiene ----
  { emoji: '🧹', label: 'Data quality',           prompt: "Scan my active sheet for data-quality issues: blank cells in calculated columns, duplicate keys, inconsistent date formats, account labels that should match but don't, numeric columns containing strings. Report by severity (high / medium / low) with cell references." },
  { emoji: '✅', label: 'Validate AASB 116',      prompt: "Validate the current sheet against AASB 116 and build a validation report worksheet. Use the rulepack tier (deterministic). Format per the report-formatting style guide." },
  { emoji: '🔎', label: 'Detect standard',        prompt: "Detect which AASB standard is most likely to apply to the data on my active sheet, and tell me whether you'd validate it via rulepack or AI-interpretive mode." },
  { emoji: '🏷', label: 'List standards',         prompt: "What AASB standards can you cover? Which are rulepack-grade vs AI-interpretive?" },
];

/**
 * Inline tool-call chip rendered in the assistant bubble.
 *
 * Phase A.2 — friendly labels. Most tools render their friendly verb-phrase
 * via toolLabel(), with the technical name on hover (title attribute) for
 * power-user debugging.
 *
 * Phase A.1 — execute_office_js is special: it renders the agent-supplied
 * `description` as the primary label and exposes a collapsible code block.
 * Opaqueness for the one tool that runs arbitrary code is unacceptable, so
 * the actual JS is always one click away from the user.
 */
function ToolCallChip({ call }: { call: ToolCallDisplay }): JSX.Element {
  const [open, setOpen] = useState(false);

  // Phase A.3 — ask_user inline buttons. Subscribe to the pending-question
  // store so the chip re-renders when the user clicks (the entry is removed)
  // and so it switches from "buttons" to "answered" state without a parent
  // re-render. We always call useSyncExternalStore (Rules of Hooks) and let
  // the conditional below decide whether to use its result.
  const pending = useSyncExternalStore(
    subscribePendingAskUser,
    () => (call.id ? getPendingAskUser(call.id) : null),
  );

  if (call.name === 'ask_user' && call.input && typeof call.input === 'object') {
    const inp = call.input as { question?: unknown; options?: unknown };
    const question = typeof inp.question === 'string' ? inp.question : '';
    const fallbackOptions = Array.isArray(inp.options)
      ? (inp.options.filter((o): o is string => typeof o === 'string'))
      : [];
    const isPending = pending !== null && call.id !== undefined;
    const options = isPending && pending ? pending.options : fallbackOptions;
    const onPick = (opt: string) => {
      if (call.id) resolveAskUser(call.id, opt);
    };
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        marginBottom: 6,
        padding: 8,
        background: '#FFF8E1',
        border: '1px solid #F2C94C',
        borderRadius: 4,
      }}>
        <div style={{ fontSize: 13, color: '#5D4037' }}>{question}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {options.map((opt, j) => (
            <button
              key={j}
              type="button"
              disabled={!isPending}
              onClick={() => onPick(opt)}
              style={{
                background: isPending ? '#FFFFFF' : '#F2F2F2',
                border: '1px solid ' + (isPending ? '#1F3864' : '#D0D0D0'),
                color: isPending ? '#1F3864' : '#666',
                borderRadius: 4,
                padding: '4px 10px',
                font: 'inherit',
                fontSize: 12,
                cursor: isPending ? 'pointer' : 'default',
              }}
            >{opt}</button>
          ))}
        </div>
        {!isPending && (
          <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>Answered.</div>
        )}
      </div>
    );
  }

  if (call.name === 'execute_office_js' && call.input && typeof call.input === 'object') {
    const inp = call.input as { description?: unknown; code?: unknown };
    const description = typeof inp.description === 'string' ? inp.description : '(no description)';
    const code = typeof inp.code === 'string' ? inp.code : '';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4 }}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            background: '#F2F2F2',
            border: '1px solid #D0D0D0',
            borderRadius: 4,
            padding: '4px 8px',
            font: 'inherit',
            fontSize: 12,
            cursor: 'pointer',
            textAlign: 'left',
          }}
          title="execute_office_js — click to view the JavaScript that ran in your workbook"
        >
          ⚙ <strong>{description}</strong> <span style={{ color: '#888' }}>{open ? '▼' : '▶'} code</span>
        </button>
        {open && code && (
          <pre style={{
            background: '#1E1E1E',
            color: '#E8E8E8',
            padding: 8,
            borderRadius: 4,
            fontSize: 11,
            lineHeight: 1.4,
            overflowX: 'auto',
            maxHeight: 240,
            margin: 0,
            whiteSpace: 'pre',
          }}>
            <code>{code}</code>
          </pre>
        )}
      </div>
    );
  }
  return <span
    title={call.name}
    style={{
      display: 'inline-block',
      background: '#F2F2F2',
      border: '1px solid #D0D0D0',
      borderRadius: 4,
      padding: '2px 6px',
      fontSize: 12,
      marginRight: 4,
      marginBottom: 4,
    }}>⚙ {toolLabel(call.name)}</span>;
}

export function App() {
  const [consented, setConsented] = useState(false);
  const [input, setInput] = useState('');
  const [display, setDisplay] = useState<DisplayMsg[]>([]);
  const [history, setHistory] = useState<Message[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 2026-05-28 — Attached reference documents (PDF v0.5). Persist across
  // chat turns within one conversation; cleared on "new chat" / convo switch.
  // Backend has 4-hour TTL; if the doc expires server-side, the chat
  // controller emits a 'document_warning' event the user can act on.
  //
  // 2026-05-28 (later) — closure-safety: also mirror to a ref so onSend
  // ALWAYS reads the latest value at click time, not the value captured
  // when the handler was created. CloudWatch confirmed a real production
  // failure where the chip was visible (state was non-empty) but onSend
  // saw an empty array (closure was stale). The ref bypasses that.
  const [attachedDocs, _setAttachedDocsState] = useState<UploadedDocument[]>([]);
  const attachedDocsRef = useRef<UploadedDocument[]>([]);
  const setAttachedDocs = (updater: UploadedDocument[] | ((prev: UploadedDocument[]) => UploadedDocument[])) => {
    _setAttachedDocsState((prev) => {
      const next = typeof updater === 'function' ? (updater as (prev: UploadedDocument[]) => UploadedDocument[])(prev) : updater;
      attachedDocsRef.current = next;  // mirror SYNCHRONOUSLY before React re-renders
      return next;
    });
  };
  const [docUploading, setDocUploading] = useState(false);
  const documentInputRef = useRef<HTMLInputElement>(null);
  // Move 5 follow-up Step 10 — quick actions are now a popover dropdown
  // that opens upward from the row above the input. Default closed so the
  // taskpane chrome stays minimal; user opens with a single click. Click
  // outside the popover or on the trigger again closes it.
  const [showQuickActions, setShowQuickActions] = useState(false);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const [contextUsage, setContextUsage] = useState<ContextUsage | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationMeta[]>([]);
  const [showConversations, setShowConversations] = useState(false);
  const [ingest, setIngest] = useState<IngestSummary | null>(null);
  const [ingestStatus, setIngestStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardBusy, setWizardBusy] = useState(false);
  // Phase C.4 — trust warning. Once acknowledged, persists in localStorage
  // so the user only sees it on the first ingest of any device. The modal
  // gates onIngest; once dismissed-with-continue, the original onIngest runs.
  const [trustAcknowledged, setTrustAcknowledged] = useState<boolean>(() => {
    try { return localStorage.getItem(TRUST_ACK_KEY) === '1'; } catch { return false; }
  });
  const [trustModalOpen, setTrustModalOpen] = useState(false);
  // Phase C.1 + C.2 — user profile + Settings modal.
  // Loaded once on mount and held in component state so the chat service
  // can read it synchronously per-turn without touching localStorage.
  const [userProfile, setUserProfile] = useState<UserProfile>(() => loadUserProfile());
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 2026-06-16 — Discussion / Analysis mode. When on, the Build<->Discuss
  // toggle is set to Discuss and chat turns carry discussionMode=true, so the
  // backend appends the DISCUSSION_ANALYSIS protocol and reasons in chat
  // instead of building a worksheet. Persisted so it survives a task-pane
  // reload (Excel reloads the pane often). Set by the toggle and /discuss.
  const [discussionMode, setDiscussionModeState] = useState<boolean>(() => {
    try { return localStorage.getItem('practiceiq.discussionMode.v1') === '1'; } catch { return false; }
  });
  const setDiscussionMode = (on: boolean | ((v: boolean) => boolean)) => {
    setDiscussionModeState(prev => {
      const next = typeof on === 'function' ? on(prev) : on;
      try { localStorage.setItem('practiceiq.discussionMode.v1', next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  };
  // Phase C.3 — session log toggle. Hooked into office.ts in the next step.
  const [sessionLogEnabled, setSessionLogEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('practiceiq.sessionLog.v1') === '1'; } catch { return false; }
  });
  // Phase B.1 — slash command menu. Opens when input starts with "/";
  // filters skills by the typed prefix; Up/Down navigates; Enter dispatches.
  const [slashIndex, setSlashIndex] = useState(0);
  // PR B — access wall state. Set when the backend returns 403 with one of
  // the entitlement gate's error codes. While set, the chat UI is hidden
  // and the wall is rendered in its place.
  const [accessWall, setAccessWall] = useState<{ reason: AccessWallReason; message: string; contactEmail: string } | null>(null);
  // 2026-05-25 — auth profile incl. plan tier. Fetched once on mount; used
  // by the header badge and feature-gating banners. Null while loading or
  // when the user isn't provisioned (PlansWelcome handles that case).
  const [authProfile, setAuthProfile] = useState<AuthProfile | null>(null);
  // 2026-05-25 — feature-gate banner. Set when a user picks a slash skill
  // their plan tier doesn't cover. Cleared when they dismiss or upgrade.
  const [upsellTrigger, setUpsellTrigger] = useState<{ featureName: string; requiredTier: PlanTier } | null>(null);
  // Layer-3-#1: server-side checkpoints for cross-device resume.
  // Ghost entries are conversations that exist on the server but not in
  // localStorage — the user started them on another device.
  const [serverCheckpoints, setServerCheckpoints] = useState<CheckpointListItem[]>([]);
  // Throttle for the auto-save (#2) — don't POST more than once / 30 s per convo.
  const lastServerSaveRef = useRef<Map<string, number>>(new Map());
  // 2026-06-16 — content signature of the last server-saved transcript per
  // convo. We save only when the signature CHANGES, so switching to / reopening
  // a conversation (incl. cross-device) never re-POSTs and never clobbers the
  // durable record with degraded fields. Replaces a time throttle (which could
  // drop the final state of a turn).
  const lastSavedSigRef = useRef<Map<string, string>>(new Map());

  // Sync the workbook session id into the office.ts dispatcher so the
  // query_snapshot proxy knows where to point. Triggers on every ingest
  // change (including "load conversation that has an ingest" on mount).
  useEffect(() => {
    setQuerySnapshotSession(ingest?.sessionId ?? null);
  }, [ingest?.sessionId]);

  // 2026-05-25 — fetch auth profile (plan tier + role) on mount so the
  // header badge can render. Best-effort: failure is silent (no badge
  // shown). Cached for the session by fetchAuthProfile().
  useEffect(() => {
    let cancelled = false;
    fetchAuthProfile().then(p => {
      if (!cancelled) setAuthProfile(p);
    });
    return () => { cancelled = true; };
  }, []);

  // Phase C.3 — mirror the session-log toggle into the office.ts module.
  // It's read by the runTool dispatcher on every tool completion, so it
  // needs to be in sync with React state.
  useEffect(() => {
    setOfficeSessionLogEnabled(sessionLogEnabled);
  }, [sessionLogEnabled]);

  // Move 5 follow-up Step 12 — auto-clear ingest panel when any backend
  // tool reports the snapshot has expired (60-min server TTL). Without
  // this, the green ✓ INGESTED panel stays visible from localStorage
  // long after the server forgot the snapshot, and the user gets
  // confusing "session not found" messages from the agent with no
  // obvious way to recover.
  useEffect(() => {
    setSessionExpiredCallback(() => {
      setIngest(null);
      setIngestStatus('idle');
      clearAllSnapshots();
      clearPendingAskUser('Workbook snapshot expired — cancelled pending question.');
      setError('Workbook snapshot expired (60-min server cache). Click ⬆ Ingest workbook to refresh, then retry your last request.');
    });
    return () => setSessionExpiredCallback(null);
  }, []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    try { setConsented(localStorage.getItem(CONSENT_KEY) === '1'); } catch { /* ignore */ }
    migrateLegacyIfNeeded();
    const list = loadConversationsList();
    setConversations(list);
    const aId = getActiveConvoId();
    if (aId) {
      const c = loadConversation(aId);
      if (c) {
        setActiveId(aId);
        setHistory(c.history);
        setDisplay(c.display.map(m => ({ ...m, streaming: false })));
        if (c.ingest) setIngest(c.ingest);
        return;
      }
    }
    if (list.length > 0) {
      const c = loadConversation(list[0].id);
      if (c) {
        setActiveId(list[0].id);
        setActiveConvoId(list[0].id);
        setHistory(c.history);
        setDisplay(c.display.map(m => ({ ...m, streaming: false })));
        if (c.ingest) setIngest(c.ingest);
      }
    }
  }, []);

  // Persist whenever the active conversation's content changes.
  useEffect(() => {
    if (!activeId) return;
    if (history.length === 0 && display.length === 0) return;
    const now = new Date().toISOString();
    const title = deriveTitle(history);
    const convo: Conversation = {
      id: activeId,
      title,
      history,
      display,
      createdAt: conversations.find(c => c.id === activeId)?.updatedAt ?? now,
      updatedAt: now,
      ingest: ingest ?? undefined,
    };
    saveConversation(convo);
    setConversations(prev => {
      const existing = prev.find(c => c.id === activeId);
      const updated: ConversationMeta = { id: activeId, title, updatedAt: now };
      const others = prev.filter(c => c.id !== activeId);
      const next = [updated, ...others].slice(0, MAX_CONVERSATIONS);
      // Drop any storage entries no longer in the list
      if (!existing && prev.length >= MAX_CONVERSATIONS) {
        const dropped = prev.slice(MAX_CONVERSATIONS - 1);
        for (const d of dropped) deleteConversation(d.id);
      }
      saveConversationsList(next);
      return next;
    });
  }, [history, display, activeId]);

  // 2026-06-16 — Durable, cross-device conversation history. Mirror the
  // DISPLAY transcript to the server AFTER a turn completes (gated on !busy so
  // we don't POST on every streamed token). We save ONLY when the content
  // signature changed since the last save/load — so switching to or reopening
  // a conversation (incl. a cross-device one with a rebuilt history) never
  // re-POSTs and never clobbers the durable record. Best-effort: the
  // localStorage copy above is always the offline fallback. Reading these back
  // later costs ZERO model tokens — it's a pure DB read.
  useEffect(() => {
    if (busy) return;                          // only persist a finished turn
    if (!activeId) return;
    if (display.length === 0) return;
    const sig = transcriptSig(display, history.length);
    if (lastSavedSigRef.current.get(activeId) === sig) return;  // unchanged since last save/load
    lastSavedSigRef.current.set(activeId, sig);

    const transcript = {
      title: deriveTitle(history),
      display: display.map(m => ({
        role: m.role,
        text: m.text,
        toolCalls: m.toolCalls.map(tc => ({ name: tc.name, id: tc.id, input: tc.input })),
      })),
      workbookFileName: ingest?.fileName ?? null,
      // Count rendered bubbles, not raw Bedrock messages — stable whether the
      // conversation was authored here or reopened cross-device (rebuilt
      // history is shorter than the original raw Message[]).
      messageCount: display.length,
    };
    void putConversationApi(activeId, transcript, new Date().toISOString());
  }, [busy, history, display, activeId, ingest]);

  // Layer-3-#2: auto-checkpoint at the cliff.
  //
  // Trigger conditions (any of):
  //   - context_usage rawPct ≥ 0.5 AND history has ≥ 4 messages
  //   - history has ≥ 30 messages (long conversations always check pointed)
  // Throttled to ≤ 1 POST per conversation per 30s.
  //
  // The checkpoint is best-effort. If the network is down or the endpoint
  // is unreachable, the local copy is still good — the user can still hit
  // ↪ for the local-only resume flow.
  useEffect(() => {
    if (!activeId) return;
    if (history.length === 0) return;
    const rawPct = contextUsage?.rawPct ?? contextUsage?.pct ?? 0;
    const longEnough = history.length >= 30;
    const filling = rawPct >= 0.5 && history.length >= 4;
    if (!longEnough && !filling) return;

    const now = Date.now();
    const lastSavedAt = lastServerSaveRef.current.get(activeId) ?? 0;
    if (now - lastSavedAt < 30_000) return;
    lastServerSaveRef.current.set(activeId, now);

    const title = deriveTitle(history);
    const cp = deriveCheckpoint(history, ingest, title);
    void putCheckpointApi(activeId, cp, new Date().toISOString()).then(r => {
      if (r) {
        // Refresh the cross-device list so a freshly-saved checkpoint
        // appears in any side-panel views.
        void listCheckpointsApi().then(setServerCheckpoints);
      }
    });
  }, [history, contextUsage, activeId, ingest]);

  // Layer-3-#1: pull server-side checkpoints on mount + after consent so
  // the conversations panel can show ghost entries (started on another
  // device, not in localStorage).
  useEffect(() => {
    if (!consented) return;
    void listCheckpointsApi().then(setServerCheckpoints);
    // 2026-06-16 — merge durable server-side conversations into the panel so
    // chats started on another device (or surviving a localStorage wipe)
    // appear and can be reopened. Local entries win on title/order; server
    // adds any the device doesn't already know about.
    void listConversationsApi().then(items => {
      if (items.length === 0) return;
      const tombstoned = loadDeletedConvoIds();
      setConversations(prev => {
        const known = new Set(prev.map(c => c.id));
        const additions = items
          .filter(it => !known.has(it.conversationId) && !tombstoned.has(it.conversationId))
          .map(it => ({ id: it.conversationId, title: it.title, updatedAt: it.sourceUpdatedAt }));
        if (additions.length === 0) return prev;
        // Sort by real timestamp (epoch), not string compare — server and
        // local ISO formats may differ in precision/zone suffix.
        const merged = [...prev, ...additions]
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        const next = merged.slice(0, MAX_CONVERSATIONS);
        // Free any local blobs for entries pushed past the cap (no-op for
        // server-only ids that never had a local blob).
        for (const dropped of merged.slice(MAX_CONVERSATIONS)) deleteConversation(dropped.id);
        saveConversationsList(next);
        return next;
      });
    });
  }, [consented]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [display, busy]);

  // Move 5 follow-up Step 10 — close the quick-actions popover when the
  // user clicks anywhere outside it (mousedown is fired before click, so
  // the inside click handlers still receive their event). Esc also closes.
  useEffect(() => {
    if (!showQuickActions) return;
    const onMouseDown = (e: MouseEvent) => {
      const node = quickActionsRef.current;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        setShowQuickActions(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowQuickActions(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showQuickActions]);

  const grantConsent = () => {
    try { localStorage.setItem(CONSENT_KEY, '1'); } catch { /* ignore */ }
    setConsented(true);
  };

  /**
   * Run a single user turn against the current/given conversation. Shared
   * by onSend (normal user typing) and continueFrom (Layer-3 checkpoint
   * resume). The text + baseHistory + convoId are passed explicitly so the
   * caller controls whether we build on existing history or start fresh.
   */
  const runUserTurn = async (
    text: string,
    baseHistory: Message[],
    convoId: string,
    options: { sessionId?: string | null; attachedDocsOverride?: UploadedDocument[] } = {},
  ) => {
    const userMsg: Message = { role: 'user', content: [{ type: 'text', text }] };
    const newHistory = [...baseHistory, userMsg];

    setDisplay((d) => [
      ...d,
      { role: 'user', text, toolCalls: [] },
      { role: 'assistant', text: '', toolCalls: [], streaming: true },
    ]);
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const final = await runChatTurn(newHistory, {
        onTextDelta: (delta) => {
          setDisplay((d) => {
            const next = [...d];
            const last = next[next.length - 1];
            if (last?.role === 'assistant' && last.streaming) {
              next[next.length - 1] = { ...last, text: last.text + delta };
            }
            return next;
          });
        },
        onToolUseStart: (name) => {
          setDisplay((d) => {
            const next = [...d];
            const last = next[next.length - 1];
            if (last?.role === 'assistant' && last.streaming) {
              next[next.length - 1] = { ...last, toolCalls: [...last.toolCalls, { name }] };
            }
            return next;
          });
        },
        onAssistantMessage: (msg) => {
          const toolUses = msg.content.filter((b): b is Extract<typeof b, { type: 'tool_use' }> => b.type === 'tool_use');
          if (toolUses.length > 0) {
            setDisplay((d) => {
              const next = [...d];
              // Attach inputs (e.g. execute_office_js code/description) to the
              // bubble that accumulated names via onToolUseStart, then push a
              // fresh streaming bubble for the next turn.
              for (let i = next.length - 1; i >= 0; i--) {
                const m = next[i];
                if (m.role === 'assistant' && m.streaming) {
                  const enriched: ToolCallDisplay[] = m.toolCalls.map((tc, idx) => ({
                    name: tc.name,
                    id: toolUses[idx]?.id,
                    input: toolUses[idx]?.input,
                  }));
                  next[i] = { ...m, toolCalls: enriched };
                  break;
                }
              }
              return [...next, { role: 'assistant', text: '', toolCalls: [], streaming: true }];
            });
          }
        },
        onError: (e) => setError(e),
        onContextUsage: (usage) => setContextUsage(usage),
        // 2026-05-28 — backend emits document_warning mid-stream if a
        // documentId can't be resolved (deploy / TTL eviction). Prune the
        // stale chip from state so the user can re-upload, and surface
        // a clear error. This is the SECOND line of defence — onSend()
        // already pre-flight-checks via listAttachedDocuments(), but if
        // a document expires AFTER pre-flight but BEFORE the chat reaches
        // Bedrock, this catches it.
        onDocumentWarning: (missing: string[], message: string) => {
          setAttachedDocs((docs) => docs.filter((d) => !missing.includes(d.documentId)));
          setError(message);
        },
      }, ctrl.signal, {
        sessionId: options.sessionId ?? null,
        userProfile,
        // 2026-05-28 — attach any uploaded reference documents on this turn.
        // Backend resolves each via documentStore (tenant-scoped, 4hr TTL)
        // and injects as Bedrock `document` content blocks on the user msg.
        // Source priority (defense in depth, all reading the SAME ref):
        //   1. options.attachedDocsOverride — explicit override from caller (onSend's pre-flight-pruned list)
        //   2. attachedDocsRef.current — ALWAYS up-to-date because the setAttachedDocs wrapper
        //      mirrors synchronously into the ref. NOT the state closure.
        // The previous closure-based read was where CloudWatch saw chip
        // visible but onSend dispatched empty array.
        attachedDocumentIds: (() => {
          const list = options.attachedDocsOverride ?? attachedDocsRef.current;
          console.log('[runUserTurn] computed attachedDocumentIds — count:', list.length, 'ids:', list.map((d) => d.documentId));
          return list.length > 0 ? list.map((d) => d.documentId) : undefined;
        })(),
        // 2026-06-16 — Discussion / Analysis mode. When the toggle is on, the
        // backend appends the DISCUSSION_ANALYSIS protocol and reasons in chat
        // rather than building a worksheet.
        discussionMode,
      });

      setHistory(final);
      setDisplay((d) => d.map((m) => ({ ...m, streaming: false })));
    } catch (e) {
      // PR B — surface the access wall when the entitlement gate fires.
      if (e instanceof AccessNotProvisionedError) {
        setAccessWall({ reason: e.reason, message: e.message, contactEmail: e.contactEmail });
      } else {
        setError((e as Error).message ?? 'Something went wrong.');
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
    void convoId; // referenced only so the persistence effect picks up the id
  };

  const onSend = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setError(null);
    setInput('');

    // First message of a brand-new chat: mint an id now so persistence works.
    let convoId = activeId;
    if (!convoId) {
      convoId = newConvoId();
      setActiveId(convoId);
      setActiveConvoId(convoId);
    }

    // 2026-05-28 — Pre-flight check on attached documents.
    // Read from REF, not state-closure. CloudWatch proved a case where
    // chip was visibly green but the closure-captured `attachedDocs` was
    // empty at click time, causing the chat to send without docs and
    // Claude to respond "I don't see a PDF". The ref is updated
    // synchronously inside the setAttachedDocs wrapper so it always
    // reflects the latest committed state.
    const docsAtClick = attachedDocsRef.current;
    let docsForThisTurn: UploadedDocument[] = docsAtClick;
    console.log('[onSend] start — chip-state-count:', docsAtClick.length, 'doc-ids:', docsAtClick.map((d) => d.documentId));
    if (docsAtClick.length > 0) {
      try {
        const serverDocs = await listAttachedDocuments();
        const serverIds = new Set(serverDocs.map((d) => d.documentId));
        const stale = docsAtClick.filter((d) => !serverIds.has(d.documentId));
        if (stale.length > 0) {
          const staleNames = stale.map((d) => d.fileName).join(', ');
          const freshDocs = docsAtClick.filter((d) => serverIds.has(d.documentId));
          setAttachedDocs(freshDocs);
          docsForThisTurn = freshDocs;
          if (freshDocs.length === 0) {
            setError(
              `The document(s) "${staleNames}" expired server-side (typically after a backend deploy or after 4 hours). Re-upload using the 📎 button and resend.`,
            );
            return;
          }
          setError(
            `Heads up: "${staleNames}" expired server-side and was excluded from this turn. Re-upload if you need it.`,
          );
        }
      } catch (e) {
        // List call failed — non-fatal. Proceed with what the user has;
        // if the chips are genuinely stale, the backend will emit a
        // document_warning event we'll handle separately.
        console.warn('[onSend] document pre-flight check failed; sending anyway', e);
      }
    }

    console.log('[onSend] dispatching with docsForThisTurn-count:', docsForThisTurn.length, 'doc-ids:', docsForThisTurn.map((d) => d.documentId));
    await runUserTurn(text, history, convoId, {
      sessionId: ingest?.sessionId ?? null,
      attachedDocsOverride: docsForThisTurn,
    });
  };

  /**
   * Move 5 Step 1 — Dashboard wizard submit handler.
   *
   * The questionnaire bypasses the agent loop entirely: answers map
   * deterministically to archetype + theme + params, the backend builder
   * runs, the plan executes in Excel, and we surface the result as a
   * synthetic assistant message in the chat history so the user sees
   * what was built (and any validation issues) right where they expect to.
   */
  const onWizardSubmit = async (answers: WizardAnswers) => {
    if (busy || wizardBusy) return;
    if (!ingest) {
      setError('Ingest the workbook first — the wizard needs the snapshot to build the dashboard.');
      return;
    }
    setError(null);
    setWizardBusy(true);

    // Mint a conversation if there isn't one — the wizard run becomes the
    // first turn of a fresh chat.
    let convoId = activeId;
    if (!convoId) {
      convoId = newConvoId();
      setActiveId(convoId);
      setActiveConvoId(convoId);
    }

    const request = questionnaireToScaffold(answers);
    const userPrompt = formatWizardPrompt(answers, request.archetype);

    // Show the user-side bubble immediately so the chat reflects intent.
    setDisplay(d => [
      ...d,
      { role: 'user', text: userPrompt, toolCalls: [] },
      { role: 'assistant', text: '', toolCalls: [{ name: 'build_scaffold' }], streaming: true },
    ]);

    // Move 5 Step 6 (Audit fix B1) — guarantee the wizard always closes and
    // wizardBusy clears, even if buildDashboardFromWizard or one of the
    // post-build setState callbacks throws. Without this finally, an unhandled
    // exception leaves the modal locked with no escape (the cancel button is
    // gated by !wizardBusy). Same anti-pattern as the Move 4 deferred Office.js
    // hotfixes — always close the loop in finally.
    try {
      // Phase C.2 — pass userProfile so the wizard's commentary slot
      // honours custom instructions.
      const result = await buildDashboardFromWizard({ ...request, userProfile });

      const userMsg: Message = { role: 'user', content: [{ type: 'text', text: userPrompt }] };
      const assistantText = formatWizardResultMarkdown(result);
      const assistantMsg: Message = { role: 'assistant', content: [{ type: 'text', text: assistantText }] };
      setHistory(h => [...h, userMsg, assistantMsg]);
      setDisplay(d => {
        const next = [...d];
        // Replace the last (streaming) assistant entry with the final text.
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].streaming) {
            next[i] = { role: 'assistant', text: assistantText, toolCalls: [{ name: 'build_scaffold' }], streaming: false };
            break;
          }
        }
        return next;
      });

      if (!result.ok) {
        setError(result.error ?? 'Wizard build failed');
      } else if (result.validation && !result.validation.passed) {
        setError(`Build finished but validation flagged ${result.validation.issues.length} issue(s) — see the chat panel.`);
      }
    } catch (e) {
      const msg = (e as Error).message ?? 'Wizard build threw an unexpected error.';
      setError(`Wizard build error: ${msg}`);
      // Drop the streaming-state assistant bubble so the chat doesn't get
      // stuck rendering a phantom "Thinking…" forever.
      setDisplay(d => d.map(m => ({ ...m, streaming: false })));
    } finally {
      setWizardBusy(false);
      setWizardOpen(false);
    }
    void convoId;
  };

  /**
   * Layer-3 resume: build a checkpoint from a (typically long, possibly
   * dead) conversation, mint a fresh conversation, seed it with a single
   * primer message, and auto-fire the first turn so the assistant
   * immediately reports what's done / what's open / what's next.
   *
   * The fresh conversation does NOT inherit the source conversation's
   * server-side ingest sessionId (it has likely expired). The primer
   * tells the assistant to re-snapshot the workbook before reporting,
   * and prompts the user to re-ingest if they want ingested-mode tools.
   */
  const continueFrom = async (sourceId: string) => {
    if (busy) return;

    // Try local first (the common case). Fall back to the server-side
    // checkpoint store for ghost entries (started on another device,
    // never persisted to this browser's localStorage).
    let primer = '';
    const source = loadConversation(sourceId);
    if (source) {
      const { history: primerHistory } = buildResumeMessage(
        source.history,
        source.ingest,
        source.title || 'previous chat',
      );
      const primerText = primerHistory[0]?.content?.[0];
      if (primerText && primerText.type === 'text') primer = primerText.text;
    } else {
      const cp = await fetchCheckpointApi(sourceId);
      if (cp) primer = renderCheckpointAsPrimer(cp);
    }
    if (!primer) {
      setError('Could not load that conversation to continue from it. The checkpoint may have expired (30-day TTL).');
      return;
    }

    // Mint the new conversation up-front so the persistence effect attaches
    // the primer + assistant reply to it from the very first state update.
    const id = newConvoId();
    setHistory([]);
    setDisplay([]);
    setError(null);
    setContextUsage(null);
    setIngest(null);
    setIngestStatus('idle');
    setAttachedDocs([]);  // 2026-05-28 — clear document attachments on new chat
    clearAllSnapshots();
    clearPendingAskUser('Started a new chat — cancelled pending question.');
    setActiveId(id);
    setActiveConvoId(id);
    setShowConversations(false);

    // Auto-fire the first turn. The primer goes into display as a normal
    // user bubble so the user can see exactly what the assistant is
    // working from. No history bloat carries over — this is a clean
    // chat seeded with one focused note.
    await runUserTurn(primer, [], id);
  };

  /**
   * Layer-3-#2 cliff button: when the meter shows the user is close to
   * the wall, this fires the same flow as ↪ on the active conversation
   * — derive a checkpoint, mint a fresh chat, auto-fire the first turn.
   * Saves the checkpoint server-side first so the resume is durable.
   */
  const continueFromActiveNow = async () => {
    if (busy || !activeId || history.length === 0) return;
    const title = deriveTitle(history);
    const cp = deriveCheckpoint(history, ingest, title);
    // Synchronous best-effort: persist before swapping. If the server is
    // unreachable, the local localStorage record still has the source
    // history so a manual ↪ later still works.
    await putCheckpointApi(activeId, cp, new Date().toISOString());
    await continueFrom(activeId);
  };

  const onStop = () => {
    abortRef.current?.abort();
    clearPendingAskUser('Cancelled — pending question discarded.');
  };

  // Phase B.1 — slash skills dispatcher. Routes the chosen Skill to a
  // deterministic action (wizard, scaffold, commentary, etc.). Skills whose
  // deterministic builder hasn't shipped (forecast, clean_data) surface an
  // honest "in development" message — we deliberately do NOT fall back to
  // an agent prompt because that's the hallucination surface Phase B exists
  // to remove.
  const dispatchSkill = async (sk: Skill): Promise<void> => {
    if (busy || wizardBusy) return;
    // 2026-05-25 — feature gate. If this skill requires a higher tier than
    // the user is on, surface the upsell banner and don't execute. The
    // banner has a "Talk to us" link and a dismiss; we don't auto-run on
    // dismiss to keep the gate honest.
    const requiredTier = requiredTierForSkill(sk);
    if (requiredTier && !meetsTier(authProfile?.plan ?? null, requiredTier)) {
      setUpsellTrigger({ featureName: sk.label, requiredTier });
      return;
    }
    if (sk.kind === 'wizard') {
      if (!ingest) {
        setError('Ingest the workbook first — the wizard needs the snapshot to build the dashboard.');
        return;
      }
      setInput('');
      setWizardOpen(true);
      return;
    }
    if (sk.kind === 'scaffold' && sk.archetype) {
      if (!ingest) {
        setError(`Ingest the workbook first — ${sk.slash} needs a snapshot to build from.`);
        return;
      }
      setInput('');
      await runScaffoldSkill(sk);
      return;
    }
    if (sk.kind === 'commentary') {
      if (!ingest) {
        setError(`Ingest the workbook first — ${sk.slash} needs a snapshot to draw numbers from.`);
        return;
      }
      setInput('');
      await runCommentarySkill(sk);
      return;
    }
    if (sk.kind === 'forecast') {
      // Phase B.2 — /forecast routes to the deterministic forecast_model scaffold.
      if (!ingest) {
        setError(`Ingest the workbook first — ${sk.slash} needs a snapshot to project from.`);
        return;
      }
      setInput('');
      await runForecastSkill(sk);
      return;
    }
    if (sk.kind === 'clean_data') {
      // Phase B.6 — /clean-data routes to the deterministic data_quality_report scaffold.
      if (!ingest) {
        setError(`Ingest the workbook first — ${sk.slash} needs a snapshot to scan.`);
        return;
      }
      setInput('');
      await runCleanDataSkill(sk);
      return;
    }
    if (sk.kind === 'discuss') {
      // 2026-06-16 — /discuss flips the Build<->Discuss toggle to Discuss and
      // focuses the input. No build, no auto-send — the user types their
      // reasoning question and the backend answers in chat (grounded in the
      // ingested workbook when present). Strip a bare leading "/discuss" the
      // user may have typed so it doesn't get sent as the prompt.
      setDiscussionMode(true);
      setInput(i => i.replace(/^\/discuss\b\s*/i, ''));
      inputRef.current?.focus();
      return;
    }
    if (sk.kind === 'prompt' && sk.prompt) {
      setInput(sk.prompt);
      inputRef.current?.focus();
      return;
    }
  };

  /** Phase B.3 /management-report — call buildDashboardFromWizard with a fixed archetype. */
  const runScaffoldSkill = async (sk: Skill) => {
    if (!sk.archetype) return;
    let convoId = activeId;
    if (!convoId) {
      convoId = newConvoId();
      setActiveId(convoId);
      setActiveConvoId(convoId);
    }
    const userPrompt = sk.slash;
    setDisplay(d => [
      ...d,
      { role: 'user', text: userPrompt, toolCalls: [] },
      { role: 'assistant', text: '', toolCalls: [{ name: 'build_scaffold' }], streaming: true },
    ]);
    setWizardBusy(true);
    try {
      const result = await buildDashboardFromWizard({
        archetype: sk.archetype,
        theme: sk.theme ?? 'corporate_navy',
        includeCommentary: true,
        includeAuditPanel: false,
        userProfile,  // Phase C.2 — slash builders honour custom instructions
      });
      const userMsg: Message = { role: 'user', content: [{ type: 'text', text: userPrompt }] };
      const assistantText = formatWizardResultMarkdown(result);
      const assistantMsg: Message = { role: 'assistant', content: [{ type: 'text', text: assistantText }] };
      setHistory(h => [...h, userMsg, assistantMsg]);
      setDisplay(d => {
        const next = [...d];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].streaming) {
            next[i] = { role: 'assistant', text: assistantText, toolCalls: [{ name: 'build_scaffold' }], streaming: false };
            break;
          }
        }
        return next;
      });
      if (!result.ok) setError(result.error ?? 'Build failed.');
    } catch (e) {
      setError(`${sk.slash} failed: ${(e as Error).message}`);
    } finally {
      setWizardBusy(false);
    }
    void convoId;
  };

  /** Phase B.6 /clean-data — call the deterministic data_quality_report scaffold. */
  const runCleanDataSkill = async (sk: Skill) => {
    let convoId = activeId;
    if (!convoId) {
      convoId = newConvoId();
      setActiveId(convoId);
      setActiveConvoId(convoId);
    }
    setDisplay(d => [
      ...d,
      { role: 'user', text: sk.slash, toolCalls: [] },
      { role: 'assistant', text: '', toolCalls: [{ name: 'build_scaffold' }], streaming: true },
    ]);
    setWizardBusy(true);
    try {
      const result = await buildDashboardFromWizard({
        archetype: 'data_quality_report',
        theme: 'corporate_navy',
        includeCommentary: false,
        includeAuditPanel: false,
        userProfile,
      });
      const userMsg: Message = { role: 'user', content: [{ type: 'text', text: sk.slash }] };
      const assistantText = formatWizardResultMarkdown(result);
      const assistantMsg: Message = { role: 'assistant', content: [{ type: 'text', text: assistantText }] };
      setHistory(h => [...h, userMsg, assistantMsg]);
      setDisplay(d => {
        const next = [...d];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].streaming) {
            next[i] = { role: 'assistant', text: assistantText, toolCalls: [{ name: 'build_scaffold' }], streaming: false };
            break;
          }
        }
        return next;
      });
      if (!result.ok) setError(result.error ?? 'Data-quality scan failed.');
    } catch (e) {
      setError(`${sk.slash} failed: ${(e as Error).message}`);
    } finally {
      setWizardBusy(false);
    }
    void convoId;
  };

  /** Phase B.2 /forecast — call the deterministic forecast_model scaffold. */
  const runForecastSkill = async (sk: Skill) => {
    let convoId = activeId;
    if (!convoId) {
      convoId = newConvoId();
      setActiveId(convoId);
      setActiveConvoId(convoId);
    }
    setDisplay(d => [
      ...d,
      { role: 'user', text: sk.slash, toolCalls: [] },
      { role: 'assistant', text: '', toolCalls: [{ name: 'build_scaffold' }], streaming: true },
    ]);
    setWizardBusy(true);
    try {
      const result = await buildDashboardFromWizard({
        archetype: 'forecast_model',
        theme: sk.theme ?? 'corporate_navy',
        includeCommentary: false,
        userProfile,
        includeAuditPanel: false,
      });
      const userMsg: Message = { role: 'user', content: [{ type: 'text', text: sk.slash }] };
      const assistantText = formatWizardResultMarkdown(result);
      const assistantMsg: Message = { role: 'assistant', content: [{ type: 'text', text: assistantText }] };
      setHistory(h => [...h, userMsg, assistantMsg]);
      setDisplay(d => {
        const next = [...d];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].streaming) {
            next[i] = { role: 'assistant', text: assistantText, toolCalls: [{ name: 'build_scaffold' }], streaming: false };
            break;
          }
        }
        return next;
      });
      if (!result.ok) setError(result.error ?? 'Forecast build failed.');
    } catch (e) {
      setError(`${sk.slash} failed: ${(e as Error).message}`);
    } finally {
      setWizardBusy(false);
    }
    void convoId;
  };

  /** Phase B.4 /commentary — call /api/workbook/commentary directly; render paragraphs as a chat bubble. */
  const runCommentarySkill = async (sk: Skill) => {
    let convoId = activeId;
    if (!convoId) {
      convoId = newConvoId();
      setActiveId(convoId);
      setActiveConvoId(convoId);
    }
    setDisplay(d => [
      ...d,
      { role: 'user', text: sk.slash, toolCalls: [] },
      { role: 'assistant', text: '', toolCalls: [{ name: 'generate_commentary' }], streaming: true },
    ]);
    setWizardBusy(true);
    try {
      const result = await runCommentary({ archetype: 'board_pl_monthly', userProfile });
      const text = result.ok
        ? (result.paragraphs ?? []).join('\n\n') || '(commentary returned no paragraphs)'
        : `Commentary failed: ${result.error ?? 'unknown error'}`;
      const userMsg: Message = { role: 'user', content: [{ type: 'text', text: sk.slash }] };
      const assistantMsg: Message = { role: 'assistant', content: [{ type: 'text', text }] };
      setHistory(h => [...h, userMsg, assistantMsg]);
      setDisplay(d => {
        const next = [...d];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].streaming) {
            next[i] = { role: 'assistant', text, toolCalls: [{ name: 'generate_commentary' }], streaming: false };
            break;
          }
        }
        return next;
      });
      if (!result.ok) setError(result.error ?? 'Commentary failed.');
    } catch (e) {
      setError(`${sk.slash} failed: ${(e as Error).message}`);
    } finally {
      setWizardBusy(false);
    }
    void convoId;
  };

  // Slash-menu derived state (recomputed every render — cheap).
  const slashItems: Skill[] = input.startsWith('/') ? filterSkills(input) : [];
  const slashOpen = input.startsWith('/') && slashItems.length > 0 && !busy && !wizardBusy;

  // Keep slashIndex in range when the filtered list shrinks.
  useEffect(() => {
    if (slashIndex >= slashItems.length) setSlashIndex(0);
  }, [slashItems.length, slashIndex]);

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex(i => Math.min(i + 1, slashItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const picked = slashItems[slashIndex];
        if (picked) void dispatchSkill(picked);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const picked = slashItems[slashIndex];
        if (picked) void dispatchSkill(picked);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // 2026-05-28 — Same race-prevention rule as the Send button: if a
      // document upload is in flight, swallow Enter so the user can't
      // accidentally send before the chip is actually in state.
      if (docUploading) return;
      // Exact slash match (user typed the whole command + Enter without using the popover)
      const trimmed = input.trim();
      if (trimmed.startsWith('/')) {
        const exact = getSkillByExactSlash(trimmed);
        if (exact) {
          void dispatchSkill(exact);
          return;
        }
      }
      onSend();
    }
  };

  const newChat = () => {
    // Create a fresh conversation. Existing one stays in the sidebar.
    setHistory([]);
    setDisplay([]);
    setError(null);
    setContextUsage(null);
    setActiveId(null);
    setActiveConvoId(null);
    setIngest(null);
    setIngestStatus('idle');
    setAttachedDocs([]);  // 2026-05-28 — clear document attachments on new chat
    clearAllSnapshots();
    clearPendingAskUser('Started a new chat — cancelled pending question.');
    setShowConversations(false);
  };

  const switchTo = async (id: string) => {
    if (busy) return;
    if (id === activeId) { setShowConversations(false); return; }
    const c = loadConversation(id);
    if (c) {
      // Local copy present — full-fidelity switch (history + display + ingest).
      // Prime the saved-signature so the post-load save effect treats this as
      // already-persisted and does NOT re-POST it.
      lastSavedSigRef.current.set(id, transcriptSig(c.display, c.history.length));
      setActiveId(id);
      setActiveConvoId(id);
      setHistory(c.history);
      setDisplay(c.display.map(m => ({ ...m, streaming: false })));
      setError(null);
      setContextUsage(null);
      setIngest(c.ingest ?? null);
      setIngestStatus('idle');
      setAttachedDocs([]);  // 2026-05-28 — attached docs are per-conversation, not persisted
      clearAllSnapshots();
      clearPendingAskUser('Switched conversation — cancelled pending question.');
      setShowConversations(false);
      return;
    }
    // 2026-06-16 — not on this device (cross-device / wiped). Pull the durable
    // transcript from the server. We rebuild a text-only history from the
    // display so the title/count are right AND the user can keep chatting
    // with context. Heavy raw tool payloads aren't stored, so a continued
    // turn is grounded in the visible text (and re-ingest the workbook if a
    // build is needed). Reading the transcript itself cost zero model tokens.
    const t = await fetchConversationApi(id);
    if (!t) {
      setError('That conversation could not be loaded — it may have expired (90-day limit).');
      return;
    }
    const rebuiltHistory: Message[] = t.display
      .filter(m => m.text && m.text.trim())
      .map(m => ({ role: m.role, content: [{ type: 'text', text: m.text }] }));
    // Prime the saved-signature from the loaded data so the save effect treats
    // this as already-persisted and does NOT re-POST a degraded copy.
    lastSavedSigRef.current.set(id, transcriptSig(
      t.display.map(m => ({ role: m.role, text: m.text, toolCalls: m.toolCalls })),
      rebuiltHistory.length,
    ));
    setActiveId(id);
    setActiveConvoId(id);
    setHistory(rebuiltHistory);
    setDisplay(t.display.map(m => ({ ...m, streaming: false })));
    setError(null);
    setContextUsage(null);
    setIngest(null);
    setIngestStatus('idle');
    setAttachedDocs([]);
    clearAllSnapshots();
    clearPendingAskUser('Switched conversation — cancelled pending question.');
    setShowConversations(false);
  };

  /** Phase C.4 — the actual ingest, called after the trust check has cleared. */
  const performIngest = async () => {
    if (busy || ingestStatus === 'loading') return;
    setError(null);
    setIngestStatus('loading');
    try {
      const result = await ingestCurrentWorkbook();
      setIngest(result);
      setIngestStatus('idle');
      if (!activeId) {
        const id = newConvoId();
        setActiveId(id);
        setActiveConvoId(id);
      }
    } catch (e) {
      setError(`Ingest failed: ${(e as Error).message ?? 'unknown error'}`);
      setIngestStatus('error');
    }
  };

  const onIngest = async () => {
    if (busy || ingestStatus === 'loading') return;
    // Phase C.4 — gate the first ingest behind the trust warning. Once the
    // user has acknowledged on this device, subsequent ingests skip the modal.
    if (!trustAcknowledged) {
      setTrustModalOpen(true);
      return;
    }
    await performIngest();
  };

  /** Phase C.4 — user clicked "Continue" in the trust modal. */
  const confirmTrustAndIngest = async () => {
    try { localStorage.setItem(TRUST_ACK_KEY, '1'); } catch { /* private mode — modal still works */ }
    setTrustAcknowledged(true);
    setTrustModalOpen(false);
    await performIngest();
  };

  const onClearIngest = () => {
    setIngest(null);
    setIngestStatus('idle');
  };

  // Move 5 follow-up Step 12 — refresh the server-side snapshot in one
  // click. Clears the cached ingest first so the panel falls through to
  // the ingest button + 'Ingesting…' state, providing visual feedback
  // while the upload happens. onIngest then re-uploads the open workbook
  // and sets fresh ingest state on success.
  const onReingest = async () => {
    if (busy || ingestStatus === 'loading') return;
    setIngest(null);
    setError(null);
    await onIngest();
  };

  const deleteOne = (id: string) => {
    // Tombstone first so a server list-merge can't resurrect it if the
    // server-side DELETE below is offline / still in flight.
    addDeletedConvoId(id);
    deleteConversation(id);
    setConversations(prev => {
      const next = prev.filter(c => c.id !== id);
      saveConversationsList(next);
      return next;
    });
    // Best-effort delete the server-side checkpoint too — keeps the
    // cross-device list in sync. If this fails (offline), the 30-day
    // TTL will sweep it eventually.
    void deleteCheckpointApi(id).then(() => {
      setServerCheckpoints(prev => prev.filter(c => c.conversationId !== id));
    });
    // 2026-06-16 — also delete the durable conversation transcript so a
    // deleted chat doesn't reappear from the server on the next list merge.
    void deleteConversationApi(id);
    if (id === activeId) {
      setHistory([]);
      setDisplay([]);
      setActiveId(null);
      setActiveConvoId(null);
      setContextUsage(null);
      clearPendingAskUser('Conversation deleted — cancelled pending question.');
    }
  };

  if (!consented) {
    return (
      <div style={s.container}>
        <h2 style={s.h2}>Practice IQ Reporting</h2>
        <p style={{ margin: 0 }}>
          Chat with Practice IQ about the spreadsheet in front of you. Practice IQ can read
          cells you ask about, and — when you ask for a report — build a new worksheet with
          the analysis, tables and formulas. Your workbook is never scanned silently, and
          nothing leaves your machine except the messages you send.
        </p>
        <p style={s.warn}>
          <b>Do not submit</b> personal, payroll, health or client-identifiable information
          unless authorised by your organisation.
        </p>
        <p style={{ fontSize: 12, color: '#555', margin: 0 }}>
          Responses are generated by Anthropic Claude running inside Practice IQ's secure
          backend on AWS Bedrock (Sydney region). AI credentials are never stored in this
          add-in.
        </p>
        <button style={s.primaryBtn} onClick={grantConsent}>I understand — continue</button>
      </div>
    );
  }

  return (
    <div style={s.shell}>
      <DashboardWizard
        open={wizardOpen}
        busy={wizardBusy}
        workbookFileName={ingest?.fileName ?? null}
        onCancel={() => { if (!wizardBusy) setWizardOpen(false); }}
        onSubmit={onWizardSubmit}
      />
      {/* Phase C.4 — first-run trust warning. One-time per device, fires
          right before the very first /api/workbook/ingest call. */}
      {trustModalOpen && (
        <div style={s.modalBackdrop} role="dialog" aria-modal="true" aria-labelledby="trust-title">
          <div style={s.modalCard}>
            <div id="trust-title" style={s.modalTitle}>Be careful with spreadsheets you didn't write</div>
            <div style={s.modalBody}>
              <p style={{ margin: '0 0 8px 0' }}>
                Spreadsheets can contain hidden instructions designed to manipulate AI assistants.
                Only use Practice IQ with workbooks from sources you trust.
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                Practice IQ ignores instructions found inside cell values where it can, but no AI
                system is 100% immune. Practice IQ can read and write to the currently open workbook.
              </p>
              <p style={{ margin: 0, fontSize: 11, color: '#666' }}>
                You won't see this warning again on this device.
              </p>
            </div>
            <div style={s.modalActions}>
              <button
                type="button"
                style={s.modalCancelBtn}
                onClick={() => setTrustModalOpen(false)}
              >Cancel</button>
              <button
                type="button"
                style={s.modalConfirmBtn}
                onClick={() => void confirmTrustAndIngest()}
              >Continue and ingest</button>
            </div>
          </div>
        </div>
      )}
      <div style={s.header}>
        <button
          style={s.linkBtn}
          onClick={() => setShowConversations(v => !v)}
          title="Conversations"
        >
          ☰ {conversations.length > 0 ? `(${conversations.length})` : ''}
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Practice IQ Reporting
          </div>
          {authProfile?.plan && (
            <PlanBadge plan={authProfile.plan} contactEmail="hello@practiceiq.com.au" />
          )}
        </div>
        <button
          style={s.linkBtn}
          onClick={() => setSettingsOpen(true)}
          title="Settings — role, custom instructions, session log"
        >⚙</button>
        <button style={s.linkBtn} onClick={newChat} disabled={busy}>New chat</button>
      </div>

      {/* Phase C.1 + C.2 — Settings modal (role / subcategory / custom
          instructions). Also hosts the C.3 session-log toggle so all
          per-device preferences live in one place. */}
      <SettingsModal
        open={settingsOpen}
        initial={userProfile}
        sessionLogEnabled={sessionLogEnabled}
        onSessionLogToggle={(enabled) => {
          setSessionLogEnabled(enabled);
          try { localStorage.setItem('practiceiq.sessionLog.v1', enabled ? '1' : '0'); } catch { /* private mode */ }
        }}
        onSave={(profile) => {
          setUserProfile(profile);
          saveUserProfile(profile);
          setSettingsOpen(false);
        }}
        onCancel={() => setSettingsOpen(false)}
      />

      {showConversations && (
        <div style={s.convoPanel}>
          <div style={{ ...s.panelHeader, padding: '6px 14px' }}>
            <span style={{ fontSize: 11, color: '#595959', fontWeight: 600 }}>CONVERSATIONS</span>
            <button style={s.linkBtn} onClick={() => setShowConversations(false)}>close</button>
          </div>
          {conversations.length === 0 && serverCheckpoints.filter(g => !conversations.some(c => c.id === g.conversationId)).length === 0 ? (
            <div style={{ padding: '8px 14px', fontSize: 12, color: '#888' }}>No saved conversations yet.</div>
          ) : (
            <div style={s.convoList}>
              {conversations.map(c => (
                <div
                  key={c.id}
                  style={{
                    ...s.convoItem,
                    background: c.id === activeId ? '#eef4fb' : 'transparent',
                  }}
                  onClick={() => void switchTo(c.id)}
                  title={`Updated ${new Date(c.updatedAt).toLocaleString()}`}
                >
                  <div style={s.convoTitle}>{c.title || 'Untitled'}</div>
                  <div style={s.convoMeta}>{new Date(c.updatedAt).toLocaleDateString()} {new Date(c.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  <button
                    style={s.convoContinue}
                    onClick={(e) => { e.stopPropagation(); continueFrom(c.id); }}
                    disabled={busy}
                    title="Continue this work in a fresh chat. Carries forward the goal, status, sheets touched and last error — but starts with a clean context so the conversation runs smoothly."
                  >
                    ↪
                  </button>
                  <button
                    style={s.convoDelete}
                    onClick={(e) => { e.stopPropagation(); deleteOne(c.id); }}
                    title="Delete this conversation"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {/* Layer-3-#1 ghost entries — checkpoints saved on another
                  device that aren't represented in this localStorage. */}
              {(() => {
                const ghosts = serverCheckpoints.filter(g =>
                  !conversations.some(c => c.id === g.conversationId)
                );
                if (ghosts.length === 0) return null;
                return (
                  <>
                    <div style={s.ghostHeader}>FROM OTHER DEVICES</div>
                    {ghosts.map(g => (
                      <div
                        key={g.conversationId}
                        style={{ ...s.convoItem, fontStyle: 'italic', color: '#666' }}
                        onClick={() => continueFrom(g.conversationId)}
                        title={`Saved ${new Date(g.savedAt).toLocaleString()} · ${g.workbookFileName ?? 'no workbook fingerprint'}. Click to continue from this device.`}
                      >
                        <div style={s.convoTitle}>{g.sourceConversationTitle || 'Untitled'}</div>
                        <div style={s.convoMeta}>
                          {new Date(g.sourceUpdatedAt).toLocaleDateString()} · {g.messageCount} msgs · server-only
                        </div>
                        <button
                          style={s.convoContinue}
                          onClick={(e) => { e.stopPropagation(); continueFrom(g.conversationId); }}
                          disabled={busy}
                          title="Continue from this checkpoint (saved on another device)"
                        >
                          ↪
                        </button>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      <div style={s.ingestPanel}>
        {ingest ? (
          <>
            <div style={s.ingestActive}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: '#0a6e3a', fontWeight: 600 }}>
                  ✓ INGESTED — {ingest.fileName} · {ingest.sheetCount} sheet{ingest.sheetCount === 1 ? '' : 's'} · {ingest.totalCells.toLocaleString()} cells
                </div>
                <div style={{ fontSize: 10, color: '#5a5a5a', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ingest.summary}
                </div>
              </div>
              <button
                style={s.linkBtnSmall}
                onClick={onReingest}
                disabled={busy || ingestStatus === 'loading'}
                title="Re-ingest the open workbook. Useful if you've made changes in Excel since the last ingest, or if the server-side snapshot has expired (60-min cache)."
              >↻</button>
              <button style={s.linkBtnSmall} onClick={onClearIngest} title="Clear the ingested workbook (does not affect the file in Excel)">✕</button>
            </div>
          </>
        ) : (
          <button
            style={ingestStatus === 'loading' ? s.ingestBtnLoading : s.ingestBtn}
            onClick={onIngest}
            disabled={busy || ingestStatus === 'loading'}
            title="Send a snapshot of the open workbook to the backend so the AI can analyse the whole file in one call. Recommended for any task that spans more than one sheet."
          >
            {ingestStatus === 'loading' ? 'Ingesting…' : '⬆ Ingest workbook for analysis'}
          </button>
        )}
      </div>

      {contextUsage && (
        <ContextMeter
          usage={contextUsage}
          onContinueNow={activeId && history.length > 0 ? continueFromActiveNow : undefined}
        />
      )}

      {upsellTrigger && (
        <UpsellBanner
          featureName={upsellTrigger.featureName}
          requiredTier={upsellTrigger.requiredTier}
          currentTier={authProfile?.plan ?? null}
          contactEmail="hello@practiceiq.com.au"
          onDismiss={() => setUpsellTrigger(null)}
        />
      )}

      {accessWall ? (
        accessWall.reason === 'not_provisioned'
          ? <PlansWelcome contactEmail={accessWall.contactEmail} />
          : <AccessRequestWall reason={accessWall.reason} message={accessWall.message} contactEmail={accessWall.contactEmail} />
      ) : (
      <div style={s.messages} ref={scrollRef}>
        {display.length === 0 && (
          <div style={s.empty}>
            Ask about your spreadsheet — for example:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 18 }}>
              <li>"Summarise what's in my current selection."</li>
              <li>"Explain the biggest variances in this budget vs actual."</li>
              <li>"Draft board commentary for this month's P&amp;L."</li>
              <li>"What risks are visible in this cashflow?"</li>
            </ul>
          </div>
        )}
        {display.map((m, i) => (
          <div key={i} style={m.role === 'user' ? s.userBubble : s.assistantBubble}>
            {m.toolCalls.length > 0 && (
              <div style={s.toolLine}>
                {m.toolCalls.map((t, j) => (
                  <ToolCallChip key={j} call={t} />
                ))}
              </div>
            )}
            {m.text ? (
              m.role === 'assistant'
                ? (
                  <>
                    <Markdown source={m.text} />
                    {/* 2026-06-16 — pulsing caret while a text-only answer is
                        still streaming, so long reasoning responses (which
                        have no build chips to signal activity) clearly read as
                        in-progress rather than finished. */}
                    {m.streaming && <span className="piq-stream-caret" style={s.streamCaret} aria-hidden="true">▋</span>}
                  </>
                )
                : <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
            ) : (
              m.role === 'assistant' && m.streaming && m.toolCalls.length === 0 && (
                <div style={{ color: '#888' }}>{discussionMode ? 'Analysing…' : 'Thinking…'}</div>
              )
            )}
          </div>
        ))}
      </div>
      )}

      {error && <div style={s.error}>{error}</div>}

      {/* Move 5 follow-up Step 10 — quick actions dropdown.
          Trigger lives in the chrome bar above the input row. Popover
          opens upward (position: absolute; bottom: 100%) so it doesn't
          clip in a narrow taskpane. Build Dashboard is the first item
          (kind: 'wizard') and opens the modal directly; all other items
          fill the input box for the user to send. */}
      <div style={s.quickActionsTriggerRow} ref={quickActionsRef}>
        <button
          style={{ ...s.quickActionsTrigger, ...(showQuickActions ? s.quickActionsTriggerOpen : {}) }}
          onClick={() => setShowQuickActions(v => !v)}
          disabled={busy}
          aria-haspopup="menu"
          aria-expanded={showQuickActions}
        >
          ⚡ Quick actions <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>{showQuickActions ? '▾' : '▴'}</span>
        </button>
        {/* 2026-06-16 — Build<->Discuss mode toggle. Build = normal worksheet
            builder; Discuss = grounded reasoning partner in chat (no build
            unless asked). Sets discussionMode on every chat turn. */}
        <div style={s.modeToggle} role="group" aria-label="Response mode">
          <button
            type="button"
            style={{ ...s.modeToggleBtn, ...(discussionMode ? {} : s.modeToggleBtnActiveBuild) }}
            onClick={() => setDiscussionMode(false)}
            aria-pressed={!discussionMode}
            title="Build mode — Practice IQ builds dashboards, reports and formulas in your workbook."
          >
            📊 Build
          </button>
          <button
            type="button"
            style={{ ...s.modeToggleBtn, ...(discussionMode ? s.modeToggleBtnActiveDiscuss : {}) }}
            onClick={() => setDiscussionMode(true)}
            aria-pressed={discussionMode}
            title="Discuss mode — think through scenarios, assumptions and analysis in chat, grounded in your workbook. No worksheet is built unless you ask."
          >
            💬 Discuss
          </button>
        </div>
        {showQuickActions && (
          <div style={s.quickActionsPopover} role="menu">
            {QUICK_ACTIONS.map((qa, idx) => {
              if (qa.kind === 'divider') {
                return (
                  <div key={`div-${idx}`} style={s.quickActionsDivider}>
                    {qa.label && <span style={s.quickActionsDividerLabel}>{qa.label}</span>}
                  </div>
                );
              }
              if (qa.kind === 'wizard') {
                return (
                  <button
                    key={qa.label}
                    style={{ ...s.quickActionItem, ...s.quickActionItemWizard }}
                    onClick={() => {
                      setShowQuickActions(false);
                      if (!ingest) {
                        setError('Ingest the workbook first — the wizard needs the snapshot to build the dashboard.');
                        return;
                      }
                      setWizardOpen(true);
                    }}
                    disabled={busy || wizardBusy}
                    title={qa.hint}
                    role="menuitem"
                  >
                    <span style={{ marginRight: 8, fontSize: 16 }}>{qa.emoji}</span>
                    <span style={{ flex: 1, textAlign: 'left' }}>
                      <span style={{ fontWeight: 600 }}>{qa.label}</span>
                      {qa.hint && <span style={s.quickActionItemHint}>{qa.hint}</span>}
                    </span>
                  </button>
                );
              }
              return (
                <button
                  key={qa.label}
                  style={s.quickActionItem}
                  onClick={() => {
                    setInput(qa.prompt);
                    setShowQuickActions(false);
                    inputRef.current?.focus();
                  }}
                  disabled={busy}
                  title={qa.prompt}
                  role="menuitem"
                >
                  <span style={{ marginRight: 8, fontSize: 14 }}>{qa.emoji}</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>{qa.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 2026-06-16 — Discuss-mode banner. Makes the active mode unmistakable
          and tells the user the behaviour change in one line. */}
      {discussionMode && (
        <div style={s.discussBanner}>
          <span>💬</span>
          <span>Discussion mode — I'll reason through this with you in chat, grounded in your workbook. I won't build a worksheet unless you ask.</span>
        </div>
      )}

      {/* Phase B.1 — slash command popover. Anchored above the input row.
          Visible whenever the input starts with "/" and at least one skill
          matches; vanishes on dispatch (input is cleared) or when the user
          types something else. */}
      {slashOpen && (
        <div style={s.slashPopover} role="listbox" aria-label="Slash commands">
          {slashItems.map((sk, idx) => {
            const highlighted = idx === slashIndex;
            return (
              <button
                key={sk.slash}
                type="button"
                role="option"
                aria-selected={highlighted}
                onMouseEnter={() => setSlashIndex(idx)}
                onClick={() => void dispatchSkill(sk)}
                style={{
                  ...s.slashItem,
                  ...(highlighted ? s.slashItemHighlight : {}),
                }}
              >
                <span style={{ marginRight: 8, fontSize: 16 }}>{sk.emoji}</span>
                <span style={{ flex: 1, textAlign: 'left' }}>
                  <span style={{ fontWeight: 600 }}>
                    {sk.slash}{sk.comingSoon && <span style={{ marginLeft: 6, fontWeight: 400, fontSize: 11, color: '#888' }}>(in development)</span>}
                  </span>
                  <span style={s.slashItemDesc}>{sk.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* 2026-05-28 — Attached reference document chips. Sits above the
          input row so the user can see which docs are in scope for the
          next chat turn. Each chip has an X to remove.
          Visual rule: ATTACHED docs use the green "ready" style with ✓;
          UPLOADING uses a separate dashed-grey chip with ⏳ so the user
          can see at a glance whether the document is genuinely ready
          to send. Send button is also disabled while docUploading is
          true — the race window where a user clicks Send during upload
          and the doc arrives a moment too late is now closed. */}
      {(attachedDocs.length > 0 || docUploading) && (
        <div style={s.docChipsRow}>
          {attachedDocs.map((d) => (
            <span key={d.documentId} style={s.docChipReady} title={`Ready to send — ${d.fileName} (${(d.sizeBytes / 1024).toFixed(0)} KB)`}>
              <span style={s.docChipReadyDot}>✓</span> 📎 <span style={s.docChipName}>{d.fileName}</span>
              <button
                style={s.docChipRemove}
                onClick={async () => {
                  // Optimistic local remove; backend delete is best-effort.
                  setAttachedDocs((docs) => docs.filter((x) => x.documentId !== d.documentId));
                  try { await deleteAttachedDocument(d.documentId); } catch { /* ignore — backend will TTL-expire */ }
                }}
                title="Remove this document from the conversation"
                disabled={busy}
              >
                ×
              </button>
            </span>
          ))}
          {docUploading && (
            <span style={s.docChipLoading} title="Document is still uploading — Send is disabled until upload completes">⏳ Uploading… (please wait)</span>
          )}
        </div>
      )}

      <div style={s.inputRow}>
        {/* Hidden file input — triggered by the 📎 attach button. v0.5 PDF only. */}
        <input
          ref={documentInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            // Always reset the input so the same file can be re-picked after remove.
            if (e.target) e.target.value = '';
            if (!file) return;
            if (!isAcceptableUploadType(file)) {
              setError('Only PDF files are supported. DOCX and image support is coming in the next release.');
              return;
            }
            setDocUploading(true);
            setError(null);
            try {
              const uploaded = await uploadDocument(file);
              setAttachedDocs((prev) => [...prev, uploaded]);
            } catch (err) {
              setError((err as Error).message || 'Document upload failed.');
            } finally {
              setDocUploading(false);
            }
          }}
        />
        <button
          style={s.attachBtn}
          onClick={() => documentInputRef.current?.click()}
          disabled={busy || docUploading}
          title="Attach a PDF document (financial statement, tax return, audit report, etc.) for this conversation"
        >
          📎
        </button>
        <textarea
          ref={inputRef}
          style={s.textarea}
          rows={3}
          placeholder={discussionMode
            ? 'Ask a question or describe a scenario to think through… (Discuss mode — no worksheet is built)'
            : 'Ask Practice IQ about your spreadsheet… or type / for skills'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          disabled={busy}
        />
        {busy ? (
          <button style={{ ...s.stopBtn }} onClick={onStop} title="Stop the current turn">
            ◾ Stop
          </button>
        ) : (
          <button
            // 2026-05-28 — Send is ALSO disabled while a document upload is
            // in flight. Earlier bug: user clicked Send between picking the
            // PDF and setAttachedDocs landing in state. The send went out
            // with no attachedDocumentIds, the backend chat controller saw
            // an empty array, Claude responded "I don't see a PDF attached".
            // Disabling Send during upload eliminates that race entirely.
            style={{ ...s.primaryBtn, opacity: (!input.trim() || docUploading) ? 0.5 : 1 }}
            onClick={onSend}
            disabled={!input.trim() || docUploading}
            title={docUploading ? 'Wait for the document upload to finish before sending' : undefined}
          >
            {docUploading ? 'Uploading…' : 'Send'}
          </button>
        )}
      </div>
      <div style={s.footer}>
        Practice IQ can read and write in this workbook. Review changes before saving. Do not
        submit personal or client-identifiable data unless authorised.
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: 10, padding: 4 },
  shell: { display: 'flex', flexDirection: 'column', height: '100vh', padding: 0, margin: -16 },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', borderBottom: '1px solid #e5e5e5', background: '#fff',
    gap: 8,
  },
  convoPanel: {
    background: '#fff', borderBottom: '1px solid #e5e5e5',
    maxHeight: 240, overflowY: 'auto',
  },
  convoList: { display: 'flex', flexDirection: 'column' },
  convoItem: {
    position: 'relative',
    padding: '8px 14px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    fontSize: 12,
  },
  convoTitle: {
    fontWeight: 500, color: '#1f1f1f',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    paddingRight: 22,
  },
  convoMeta: { color: '#888', fontSize: 11, marginTop: 2 },
  convoContinue: {
    position: 'absolute', top: 6, right: 30,
    background: 'transparent', border: 'none', color: '#0078d4',
    cursor: 'pointer', fontSize: 14, padding: 2, fontWeight: 600,
  },
  ghostHeader: {
    fontSize: 10, fontWeight: 600, color: '#888',
    padding: '8px 14px 4px 14px',
    borderTop: '1px solid #f0f0f0',
    background: '#fafafa',
    letterSpacing: 0.4,
  },
  convoDelete: {
    position: 'absolute', top: 6, right: 8,
    background: 'transparent', border: 'none', color: '#a0a0a0',
    cursor: 'pointer', fontSize: 13, padding: 2,
  },
  messages: {
    flex: 1, overflowY: 'auto', padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: 10, background: '#fafafa',
  },
  empty: { color: '#666', fontSize: 13, background: '#fff', border: '1px solid #eee', borderRadius: 6, padding: 12 },
  userBubble: {
    alignSelf: 'flex-end', maxWidth: '90%',
    background: '#0078d4', color: '#fff',
    padding: '8px 10px', borderRadius: 8, fontSize: 13,
  },
  assistantBubble: {
    alignSelf: 'flex-start', maxWidth: '95%',
    background: '#fff', color: '#1f1f1f',
    border: '1px solid #e5e5e5',
    padding: '8px 10px', borderRadius: 8, fontSize: 13,
  },
  toolLine: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 },
  toolChip: {
    fontSize: 11, background: '#eef4fb', color: '#0953a0',
    border: '1px solid #cbe0f5', padding: '2px 6px', borderRadius: 10,
  },
  inputRow: {
    display: 'flex', gap: 6, padding: '10px 14px', alignItems: 'flex-end',
    borderTop: '1px solid #e5e5e5', background: '#fff',
  },
  textarea: {
    flex: 1, resize: 'none', fontFamily: 'inherit', fontSize: 13,
    padding: 8, border: '1px solid #ccc', borderRadius: 4,
  },
  primaryBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600,
    color: '#fff', background: '#0078d4', border: 'none',
    borderRadius: 4, cursor: 'pointer', alignSelf: 'flex-end',
  },
  // 2026-05-28 — Document attachment chips + attach button.
  docChipsRow: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    padding: '6px 14px 0 14px', background: '#fff',
    borderTop: '1px solid #f0f0f0',
  },
  // Ready chip — green tint so user can see at a glance "this WILL go
  // with the next send". Differentiates visually from the uploading chip.
  docChipReady: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#e8f5e9', color: '#1b5e20',
    border: '1px solid #a5d6a7', padding: '3px 4px 3px 8px',
    borderRadius: 12, fontSize: 11, maxWidth: 240,
  },
  docChipReadyDot: {
    fontSize: 10, fontWeight: 700, color: '#2e7d32',
    padding: '0 2px',
  },
  docChipName: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: 160, display: 'inline-block', verticalAlign: 'bottom',
  },
  docChipRemove: {
    background: 'transparent', border: 'none', color: '#1b5e20',
    cursor: 'pointer', fontSize: 14, padding: '0 4px',
    fontWeight: 600, lineHeight: 1,
  },
  // Uploading chip — visibly different (dashed grey border, italic-style
  // wording) so the user can distinguish "still uploading" from "ready".
  docChipLoading: {
    fontSize: 11, color: '#666', padding: '4px 8px',
    background: '#fafafa', border: '1px dashed #bbb', borderRadius: 12,
    fontStyle: 'italic',
  },
  attachBtn: {
    padding: '8px 10px', fontSize: 15,
    background: '#fff', border: '1px solid #ccc', borderRadius: 4,
    cursor: 'pointer', alignSelf: 'flex-end', minWidth: 36,
  },
  stopBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600,
    color: '#fff', background: '#a4262c', border: 'none',
    borderRadius: 4, cursor: 'pointer', alignSelf: 'flex-end',
  },
  linkBtn: {
    fontSize: 12, color: '#0078d4', background: 'transparent',
    border: 'none', cursor: 'pointer', padding: 0,
  },
  linkBtnSmall: {
    fontSize: 12, color: '#888', background: 'transparent',
    border: 'none', cursor: 'pointer', padding: '0 4px',
    alignSelf: 'flex-start',
  },
  ingestPanel: {
    background: '#fff', borderBottom: '1px solid #e5e5e5',
    padding: '6px 10px',
  },
  ingestBtn: {
    width: '100%', padding: '6px 10px', fontSize: 12,
    color: '#1f3864', background: '#eef4fb',
    border: '1px solid #cbe0f5', borderRadius: 4,
    cursor: 'pointer', fontWeight: 500,
  },
  ingestBtnLoading: {
    width: '100%', padding: '6px 10px', fontSize: 12,
    color: '#5a5a5a', background: '#fafafa',
    border: '1px dashed #cbd5e0', borderRadius: 4,
    cursor: 'wait', fontWeight: 500,
  },
  ingestActive: {
    display: 'flex', alignItems: 'flex-start', gap: 6,
    padding: '4px 6px',
    background: '#f0f9f1', border: '1px solid #cce8d6',
    borderRadius: 4,
  },
  // Generic panel-header (flex justify-between) — used by the conversations
  // panel and previously by the now-removed quick-actions panel.
  panelHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 4,
  },
  // ---- Move 5 follow-up Step 10 — Quick actions dropdown ----
  quickActionsTriggerRow: {
    position: 'relative',
    background: '#fff',
    borderTop: '1px solid #e5e5e5',
    padding: '4px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  // 2026-06-16 — Build<->Discuss segmented mode toggle (right of Quick actions).
  modeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #cbd5e0',
    borderRadius: 16,
    overflow: 'hidden',
    background: '#fafafa',
    flexShrink: 0,
  },
  modeToggleBtn: {
    fontSize: 12,
    padding: '5px 11px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#5a6677',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  modeToggleBtnActiveBuild: {
    background: '#1f3864',
    color: '#fff',
    fontWeight: 600,
  },
  modeToggleBtnActiveDiscuss: {
    background: '#0953a0',
    color: '#fff',
    fontWeight: 600,
  },
  streamCaret: {
    color: '#0953a0',
    marginLeft: 1,
    fontWeight: 700,
  },
  discussBanner: {
    background: '#eef4fb',
    borderTop: '1px solid #cfe0f5',
    color: '#0953a0',
    fontSize: 11.5,
    padding: '5px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  quickActionsTrigger: {
    fontSize: 12, padding: '6px 12px',
    border: '1px solid #cbd5e0',
    background: '#fafafa',
    borderRadius: 16,
    cursor: 'pointer',
    color: '#1f3864',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
  },
  quickActionsTriggerOpen: {
    background: '#eef4fb',
    borderColor: '#0078d4',
    color: '#0953a0',
  },
  quickActionsPopover: {
    position: 'absolute',
    bottom: '100%',
    left: 14,
    right: 14,
    marginBottom: 4,
    background: '#fff',
    border: '1px solid #cbd5e0',
    borderRadius: 6,
    boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
    maxHeight: 360,
    overflowY: 'auto',
    zIndex: 50,
    padding: '4px 0',
  },
  // Phase B.1 — slash command popover. Sits directly above the input box.
  slashPopover: {
    background: '#fff',
    border: '1px solid #1F3864',
    borderRadius: 6,
    boxShadow: '0 -4px 12px rgba(0,0,0,0.10)',
    margin: '0 14px 6px 14px',
    maxHeight: 280,
    overflowY: 'auto',
    padding: '4px 0',
  },
  slashItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    padding: '8px 12px',
    fontSize: 12,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#1f1f1f',
    textAlign: 'left',
  },
  slashItemHighlight: {
    background: '#EAF1FB',
  },
  slashItemDesc: {
    display: 'block',
    fontSize: 11,
    color: '#666',
    marginTop: 2,
    fontWeight: 400,
  },
  // Phase C.4 — trust warning modal styles.
  modalBackdrop: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100,
    padding: 16,
  },
  modalCard: {
    background: '#fff',
    border: '1px solid #cbd5e0',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    maxWidth: 440,
    width: '100%',
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  modalTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1F3864',
  },
  modalBody: {
    fontSize: 12,
    color: '#1f1f1f',
    lineHeight: 1.5,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  modalCancelBtn: {
    padding: '6px 14px',
    fontSize: 12,
    background: '#fff',
    border: '1px solid #cbd5e0',
    borderRadius: 4,
    cursor: 'pointer',
  },
  modalConfirmBtn: {
    padding: '6px 14px',
    fontSize: 12,
    background: '#1F3864',
    color: '#fff',
    border: '1px solid #1F3864',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 600,
  },
  quickActionItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    fontSize: 12,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#1f1f1f',
    textAlign: 'left',
  },
  quickActionItemWizard: {
    background: '#f4f9ff',
    borderBottom: '1px solid #e5e5e5',
    paddingTop: 8,
    paddingBottom: 8,
  },
  quickActionItemHint: {
    display: 'block',
    fontSize: 11,
    color: '#666',
    marginTop: 2,
    fontWeight: 400,
  },
  quickActionsDivider: {
    padding: '8px 12px 2px 12px',
    fontSize: 10,
    color: '#888',
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    borderTop: '1px solid #f0f0f0',
    marginTop: 4,
  },
  quickActionsDividerLabel: {
    fontSize: 10,
  },
  h2: { margin: 0, fontSize: 18 },
  warn: {
    background: '#fff4e5', border: '1px solid #f2a93b',
    padding: 10, borderRadius: 4, fontSize: 13, margin: 0,
  },
  error: {
    margin: '0 14px 8px 14px', background: '#fde7e9',
    border: '1px solid #d13438', color: '#a4262c',
    padding: 8, borderRadius: 4, fontSize: 12,
  },
  footer: {
    fontSize: 11, color: '#8a5b00', textAlign: 'center',
    padding: '4px 8px 8px 8px', background: '#fff',
  },
};
